// Developer Platform — Tab 2: webhook subscriptions (event-processor via
// /api/v1/webhooks-admin/*). The backend ships in parallel with this UI, so a
// 404/502 on load renders a muted "being rolled out" state instead of an error.
import React, { useEffect, useState } from 'react';
import { getApiErrorMessage, isEntitlementUnavailable } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import {
  History, Loader2, Pencil, Plus, RefreshCw, RotateCcw, Send, Trash2, Webhook,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { TruncatedText } from '@/features/shared/components/ui/TruncatedText';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/features/shared/components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/features/shared/components/ui/table';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { ActionRefusal } from '@/features/shared/components/ui/ActionRefusal';
import {
  WEBHOOK_EVENT_TYPES_FALLBACK, createWebhookSubscription, deleteWebhookSubscription, isNotEntitled, isNotRolledOut,
  listWebhookDeliveries, listWebhookEventTypes, listWebhookSubscriptions, replayWebhookDelivery, rotateWebhookSecret,
  testWebhookSubscription, updateWebhookSubscription,
  type WebhookDelivery, type WebhookSubscription,
} from '@/services/developerService';
import { FeatureNotEnabledState, RolloutPendingState, SecretModal, errMsg, fmtDateTime, inputCls, TemporarilyUnavailableState } from './common';

const SUB_STATUS_BADGE: Record<string, string> = {
  active: 'bg-success-soft text-success-strong border-transparent',
  failing: 'bg-warning-soft text-warning-strong border-transparent',
  disabled: 'bg-danger-soft text-danger-strong border-transparent',
};

const DELIVERY_STATUS_BADGE: Record<string, string> = {
  delivered: 'bg-success-soft text-success-strong border-transparent',
  pending: 'bg-muted text-muted-foreground border-border',
  failed: 'bg-warning-soft text-warning-strong border-transparent',
  dead: 'bg-danger-soft text-danger-strong border-transparent',
};

// -------------------------------------------------------- create/edit dialog
export const SubscriptionDialog: React.FC<{
  existing: WebhookSubscription | null;
  onCreated: (sub: WebhookSubscription, secret: string) => void;
  onUpdated: (patch: { url: string; description?: string; eventTypes: string[] }) => void;
  onClose: () => void;
}> = ({ existing, onCreated, onUpdated, onClose }) => {
  const { t } = useTranslation('admin');
  const [url, setUrl] = useState(existing?.url || '');
  const [description, setDescription] = useState(existing?.description || '');
  const [eventTypes, setEventTypes] = useState<string[]>(existing?.eventTypes || []);
  const [saving, setSaving] = useState(false);
  // The server's refusal (e.g. an internal endpoint URL), kept on the dialog
  // until the user edits or retries — a toast alone vanishes behind the modal.
  const [refusal, setRefusal] = useState<string | null>(null);
  // Live taxonomy from the enforcing catalog (Agent Platform Phase 2) — the
  // old hardcoded list had drifted 15 types behind the server.
  const [availableEvents, setAvailableEvents] = useState<string[]>(WEBHOOK_EVENT_TYPES_FALLBACK);
  useEffect(() => {
    listWebhookEventTypes().then(setAvailableEvents).catch(() => undefined);
  }, []);

  const allEvents = eventTypes.includes('*');

  const toggleEvent = (evt: string) =>
    setEventTypes((cur) => {
      // '*' is exclusive — it means every current + future event type.
      if (evt === '*') return cur.includes('*') ? [] : ['*'];
      const next = cur.includes(evt) ? cur.filter((e) => e !== evt) : [...cur.filter((e) => e !== '*'), evt];
      return next;
    });

  const submit = async () => {
    const cleanUrl = url.trim();
    if (!/^https:\/\/.+/i.test(cleanUrl)) {
      toast.error(t('developer.wh_url_invalid', { defaultValue: 'Enter an https:// endpoint URL.' }));
      return;
    }
    if (eventTypes.length === 0) {
      toast.error(t('developer.wh_events_required', { defaultValue: 'Select at least one event type.' }));
      return;
    }
    setSaving(true);
    setRefusal(null);
    try {
      if (existing) {
        await updateWebhookSubscription(existing.id, {
          url: cleanUrl,
          description: description.trim(),
          eventTypes,
        });
        toast.success(t('developer.wh_updated', { defaultValue: 'Webhook updated.' }));
        onUpdated({ url: cleanUrl, description: description.trim(), eventTypes });
      } else {
        const { subscription, secret } = await createWebhookSubscription({
          url: cleanUrl,
          ...(description.trim() ? { description: description.trim() } : {}),
          eventTypes,
        });
        onCreated(subscription, secret);
      }
    } catch (e: any) {
      // Shown inline on the dialog (role=alert) and kept until the user edits or retries.
      setRefusal(getApiErrorMessage(e, errMsg(e, t('developer.wh_save_failed', { defaultValue: 'Could not save the webhook.' }))));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existing
              ? t('developer.wh_edit', { defaultValue: 'Edit webhook' })
              : t('developer.wh_new', { defaultValue: 'New webhook endpoint' })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="wh-url">
              {t('developer.wh_url', { defaultValue: 'Endpoint URL' })}
            </label>
            <input
              id="wh-url"
              className={inputCls}
              value={url}
              onChange={(e) => { setUrl(e.target.value); setRefusal(null); }}
              placeholder="https://example.com/kazipeople/webhooks"
            />
          </div>
          <ActionRefusal message={refusal} onDismiss={() => setRefusal(null)} testId="webhook-save-refusal" />
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="wh-desc">
              {t('developer.wh_desc', { defaultValue: 'Description (optional)' })}
            </label>
            <input
              id="wh-desc"
              className={inputCls}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('developer.wh_desc_ph', { defaultValue: 'e.g. ERP employee sync' })}
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">
              {t('developer.wh_events', { defaultValue: 'Event types' })}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {availableEvents.map((evt) => (
                <label key={evt} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-orange-500 disabled:cursor-not-allowed"
                    checked={evt === '*' ? allEvents : eventTypes.includes(evt)}
                    onChange={() => toggleEvent(evt)}
                    disabled={evt !== '*' && allEvents}
                  />
                  <span className={`font-mono text-xs ${evt !== '*' && allEvents ? 'text-muted-foreground' : ''}`}>
                    {evt === '*' ? t('developer.wh_all_events', { defaultValue: '* (all events)' }) : evt}
                  </span>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              {t('developer.wh_events_hint', { defaultValue: 'Deliveries are unordered and retried — make your handler idempotent and verify the signature.' })}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('developer.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {existing
              ? t('developer.wh_save', { defaultValue: 'Save changes' })
              : t('developer.wh_create', { defaultValue: 'Create webhook' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --------------------------------------------------------- deliveries drawer
const DeliveriesSheet: React.FC<{
  sub: WebhookSubscription;
  onClose: () => void;
}> = ({ sub, onClose }) => {
  const { t } = useTranslation('admin');
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [replayingId, setReplayingId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    listWebhookDeliveries(sub.id, 25)
      .then(setDeliveries)
      .catch(() => setDeliveries([]))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [sub.id]);

  const onReplay = async (d: WebhookDelivery) => {
    setReplayingId(d.id);
    try {
      await replayWebhookDelivery(d.id);
      toast.success(t('developer.wh_replay_queued', { defaultValue: 'Delivery queued for replay.' }));
      load();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.wh_replay_failed', { defaultValue: 'Could not replay the delivery.' }))));
    } finally {
      setReplayingId(null);
    }
  };

  const statusLabel = (s: string) => {
    switch (s) {
      case 'delivered': return t('developer.dlv_delivered', { defaultValue: 'Delivered' });
      case 'pending': return t('developer.dlv_pending', { defaultValue: 'Pending' });
      case 'failed': return t('developer.dlv_failed', { defaultValue: 'Retrying' });
      case 'dead': return t('developer.dlv_dead', { defaultValue: 'Dead' });
      default: return s;
    }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="sm:max-w-xl w-full overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('developer.wh_deliveries_title', { defaultValue: 'Recent deliveries' })}</SheetTitle>
          <SheetDescription className="break-all">{sub.url}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="py-10 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {t('developer.wh_deliveries_loading', { defaultValue: 'Loading deliveries…' })}
            </div>
          ) : deliveries.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {t('developer.wh_no_deliveries', { defaultValue: 'No deliveries yet — events will show up here as they happen.' })}
            </p>
          ) : deliveries.map((d) => (
            <div key={d.id} className="rounded-lg border border-border p-3 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <code className="text-xs font-mono text-foreground truncate">{d.eventType}</code>
                <Badge className={`border shrink-0 ${DELIVERY_STATUS_BADGE[d.status] || DELIVERY_STATUS_BADGE.pending}`}>
                  {statusLabel(d.status)}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
                <span>
                  {t('developer.dlv_attempts', { defaultValue: '{{count}} attempts', count: d.attempts })}
                  {' · '}
                  {d.status === 'delivered' && d.deliveredAt
                    ? t('developer.dlv_delivered_at', { defaultValue: 'delivered {{when}}', when: fmtDateTime(d.deliveredAt) })
                    : t('developer.dlv_created_at', { defaultValue: 'created {{when}}', when: fmtDateTime(d.createdAt) })}
                </span>
                {d.status === 'dead' && (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => onReplay(d)} disabled={replayingId === d.id}>
                    {replayingId === d.id
                      ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      : <RotateCcw className="w-3 h-3 mr-1" />}
                    {t('developer.dlv_replay', { defaultValue: 'Replay' })}
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground font-mono truncate">{d.eventId}</p>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// --------------------------------------------------------------------- tab
export const WebhooksTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const confirm = useConfirm();

  const [subs, setSubs] = useState<WebhookSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [rolloutPending, setRolloutPending] = useState(false);
  const [notEnabledMsg, setNotEnabledMsg] = useState<string | null>(null);
  const [unavailableMsg, setUnavailableMsg] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ existing: WebhookSubscription | null } | null>(null);
  const [deliveriesFor, setDeliveriesFor] = useState<WebhookSubscription | null>(null);
  const [secret, setSecret] = useState<{ title: string; value: string } | null>(null);

  useEffect(() => {
    let alive = true;
    listWebhookSubscriptions()
      .then((list) => { if (alive) setSubs(list); })
      .catch((e) => {
        if (!alive) return;
        if (isEntitlementUnavailable(e)) setUnavailableMsg(errMsg(e, ''));
        else if (isNotEntitled(e)) setNotEnabledMsg(errMsg(e, ''));
        else if (isNotRolledOut(e)) setRolloutPending(true);
        else setLoadError(true);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const onCreated = (sub: WebhookSubscription, whSecret: string) => {
    setSubs((cur) => [sub, ...cur]);
    setDialog(null);
    setSecret({
      title: t('developer.wh_secret_title', { defaultValue: 'Webhook signing secret' }),
      value: whSecret,
    });
  };

  const onRotate = async (sub: WebhookSubscription) => {
    const ok = await confirm({
      title: t('developer.wh_rotate_title', { defaultValue: 'Rotate signing secret' }),
      message: t('developer.wh_rotate_confirm', {
        defaultValue: 'A new signing secret is issued. During the overlap window, deliveries carry signatures for both secrets so you can switch without dropping events. Continue?',
      }),
      confirmLabel: t('developer.rotate', { defaultValue: 'Rotate secret' }),
    });
    if (!ok) return;
    setBusyId(sub.id);
    try {
      const newSecret = await rotateWebhookSecret(sub.id);
      setSecret({
        title: t('developer.wh_secret_title', { defaultValue: 'Webhook signing secret' }),
        value: newSecret,
      });
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.rotate_failed', { defaultValue: 'Could not rotate the secret.' }))));
    } finally {
      setBusyId(null);
    }
  };

  const onTest = async (sub: WebhookSubscription) => {
    setBusyId(sub.id);
    try {
      const { statusCode, durationMs } = await testWebhookSubscription(sub.id);
      if (statusCode >= 200 && statusCode < 300) {
        toast.success(t('developer.wh_test_ok', {
          defaultValue: 'Test event delivered — HTTP {{code}} in {{ms}} ms.', code: statusCode, ms: durationMs,
        }));
      } else {
        toast.error(t('developer.wh_test_bad', {
          defaultValue: 'Endpoint answered HTTP {{code}} ({{ms}} ms).', code: statusCode, ms: durationMs,
        }));
      }
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.wh_test_failed', { defaultValue: 'Could not reach the endpoint.' }))));
    } finally {
      setBusyId(null);
    }
  };

  const onToggleStatus = async (sub: WebhookSubscription) => {
    const enable = sub.status === 'disabled';
    if (!enable) {
      const ok = await confirm({
        title: t('developer.wh_disable_title', { defaultValue: 'Disable webhook' }),
        message: t('developer.wh_disable_confirm', { defaultValue: 'Stop all deliveries to this endpoint? You can re-enable it later.' }),
        confirmLabel: t('developer.disable', { defaultValue: 'Disable' }),
        danger: true,
      });
      if (!ok) return;
    }
    setBusyId(sub.id);
    try {
      await updateWebhookSubscription(sub.id, { status: enable ? 'active' : 'disabled' });
      setSubs((cur) => cur.map((s) => (s.id === sub.id ? { ...s, status: enable ? 'active' : 'disabled' } : s)));
      toast.success(enable
        ? t('developer.wh_enabled', { defaultValue: 'Webhook enabled.' })
        : t('developer.wh_disabled', { defaultValue: 'Webhook disabled.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.status_failed', { defaultValue: 'Could not change the status.' }))));
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (sub: WebhookSubscription) => {
    const ok = await confirm({
      title: t('developer.wh_delete_title', { defaultValue: 'Delete webhook' }),
      message: t('developer.wh_delete_confirm', {
        defaultValue: 'Delete this endpoint and its delivery history? This cannot be undone.',
      }),
      confirmLabel: t('developer.delete', { defaultValue: 'Delete' }),
      danger: true,
    });
    if (!ok) return;
    setBusyId(sub.id);
    try {
      await deleteWebhookSubscription(sub.id);
      setSubs((cur) => cur.filter((s) => s.id !== sub.id));
      toast.success(t('developer.wh_deleted', { defaultValue: 'Webhook deleted.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.wh_delete_failed', { defaultValue: 'Could not delete the webhook.' }))));
    } finally {
      setBusyId(null);
    }
  };

  const statusLabel = (s: string) => {
    switch (s) {
      case 'active': return t('developer.status_active', { defaultValue: 'Active' });
      case 'failing': return t('developer.status_failing', { defaultValue: 'Failing' });
      case 'disabled': return t('developer.status_disabled', { defaultValue: 'Disabled' });
      default: return s;
    }
  };

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('developer.wh_loading', { defaultValue: 'Loading webhooks…' })}
      </div>
    );
  }
  if (unavailableMsg !== null) return <TemporarilyUnavailableState message={unavailableMsg} />;
  if (notEnabledMsg !== null) {
    return <FeatureNotEnabledState message={notEnabledMsg} />;
  }
  if (rolloutPending) {
    return (
      <RolloutPendingState
        message={t('developer.wh_rollout', { defaultValue: 'Webhook management is being rolled out to your workspace. Check back soon.' })}
      />
    );
  }
  if (loadError) {
    return (
      <div className="rounded-lg px-3 py-2 text-sm border bg-danger-soft border-danger text-danger-strong">
        {t('developer.wh_load_error', { defaultValue: 'Could not load webhooks.' })}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('developer.wh_subtitle', {
            defaultValue: 'Receive signed HTTPS notifications when things happen in KaziPeople. Failed deliveries retry automatically for up to 12 hours.',
          })}
        </p>
        <Button onClick={() => setDialog({ existing: null })}>
          <Plus className="w-4 h-4 mr-1" /> {t('developer.wh_new', { defaultValue: 'New webhook endpoint' })}
        </Button>
      </div>

      {subs.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-14 text-center text-muted-foreground">
            <Webhook className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm">
              {t('developer.wh_empty', { defaultValue: 'No webhook endpoints yet. Add one to get notified about employee, leave and payroll events.' })}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('developer.col_endpoint', { defaultValue: 'Endpoint' })}</TableHead>
                  <TableHead>{t('developer.col_events', { defaultValue: 'Events' })}</TableHead>
                  <TableHead>{t('developer.col_status', { defaultValue: 'Status' })}</TableHead>
                  <TableHead className="text-right">{t('developer.col_actions', { defaultValue: 'Actions' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subs.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <p className="text-sm font-mono text-foreground break-all max-w-[320px]">{s.url}</p>
                      {s.description && <TruncatedText as="p" className="text-xs text-muted-foreground max-w-[320px]">{s.description}</TruncatedText>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[260px]">
                        {(s.eventTypes || []).slice(0, 4).map((evt) => (
                          <span key={evt} className="text-[11px] font-mono text-foreground bg-muted border border-border rounded-full px-2 py-0.5">
                            {evt}
                          </span>
                        ))}
                        {(s.eventTypes || []).length > 4 && (
                          <span className="text-[11px] text-muted-foreground px-1 py-0.5">
                            {t('developer.wh_more_events', { defaultValue: '+{{count}} more', count: (s.eventTypes || []).length - 4 })}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`border ${SUB_STATUS_BADGE[s.status] || SUB_STATUS_BADGE.active}`}>
                        {statusLabel(s.status)}
                      </Badge>
                      {s.status === 'failing' && !!s.consecutiveFailures && (
                        <p className="text-[11px] text-warning mt-1">
                          {t('developer.wh_consecutive_failures', { defaultValue: '{{count}} consecutive failures', count: s.consecutiveFailures })}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onTest(s)} disabled={busyId === s.id}
                          aria-label={t('developer.wh_test', { defaultValue: 'Send test event' })} title={t('developer.wh_test', { defaultValue: 'Send test event' })}>
                          <Send className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setDeliveriesFor(s)} disabled={busyId === s.id}
                          aria-label={t('developer.wh_deliveries', { defaultValue: 'View deliveries' })} title={t('developer.wh_deliveries', { defaultValue: 'View deliveries' })}>
                          <History className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setDialog({ existing: s })} disabled={busyId === s.id}
                          aria-label={t('developer.wh_edit', { defaultValue: 'Edit webhook' })} title={t('developer.wh_edit', { defaultValue: 'Edit webhook' })}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onRotate(s)} disabled={busyId === s.id}
                          aria-label={t('developer.rotate', { defaultValue: 'Rotate secret' })} title={t('developer.rotate', { defaultValue: 'Rotate secret' })}>
                          <RefreshCw className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost"
                          className={`h-8 px-2 text-xs ${s.status === 'disabled' ? 'text-success hover:bg-green-50 dark:hover:bg-green-500/10' : ''}`}
                          onClick={() => onToggleStatus(s)} disabled={busyId === s.id}>
                          {s.status === 'disabled'
                            ? t('developer.enable', { defaultValue: 'Enable' })
                            : t('developer.disable', { defaultValue: 'Disable' })}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-danger hover:bg-red-50 dark:hover:bg-red-500/10"
                          onClick={() => onDelete(s)} disabled={busyId === s.id}
                          aria-label={t('developer.delete', { defaultValue: 'Delete' })} title={t('developer.delete', { defaultValue: 'Delete' })}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {dialog && (
        <SubscriptionDialog
          existing={dialog.existing}
          onCreated={onCreated}
          onUpdated={(patch) => {
            const id = dialog.existing?.id;
            setSubs((cur) => cur.map((s) => (s.id === id ? { ...s, ...patch } : s)));
            setDialog(null);
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {deliveriesFor && <DeliveriesSheet sub={deliveriesFor} onClose={() => setDeliveriesFor(null)} />}
      {secret && (
        <SecretModal
          title={secret.title}
          description={t('developer.wh_secret_desc', {
            defaultValue: 'Verify every delivery with this secret: HMAC-SHA256 over "timestamp.body" against the X-KaziPeople-Signature header.',
          })}
          secret={secret.value}
          onClose={() => setSecret(null)}
        />
      )}
    </div>
  );
};
